// One always-editable rate cell for the admin pricing matrix. Reads the saved
// rate from the row data and the in-progress dirty value from the
// pricing-editor slice; on Enter / blur it dispatches `saveRow`. Visual states
// (idle / dirty / saving / saved / error) are signalled by an accent border on
// the left edge so a quick scroll across the table makes attention areas
// obvious.
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, InputBase, Tooltip, CircularProgress, Typography,
} from '@mui/material';
import {
  setCellValue, revertRow,
  selectCellValue, selectIsCellDirty, selectRowState,
} from '../../../store/pricing-editor-slice.js';

// Map row status -> background + outline tint for the value cluster. The
// cluster is right-anchored (next to the number) so the marker sits adjacent
// to what changed instead of on the cell's far left edge.
const accentForStatus = (status, isDirty) => {
  if (status === 'saving') return { bg: 'info.lighter', ring: 'info.main' };
  if (status === 'error') return { bg: 'error.lighter', ring: 'error.main' };
  if (status === 'saved') return { bg: 'success.lighter', ring: 'success.main' };
  if (isDirty) return { bg: 'warning.lighter', ring: 'warning.main' };
  return { bg: 'transparent', ring: 'transparent' };
};

const PricingRateCell = ({
  rowKey, cellIndex, savedRate, ariaLabel,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const rate = Number(savedRate ?? 0);

  const value = useSelector(selectCellValue(rowKey, cellIndex, rate));
  const isDirty = useSelector(selectIsCellDirty(rowKey, cellIndex, rate));
  const rowState = useSelector(selectRowState(rowKey));
  const rowStatus = rowState?.status ?? 'idle';
  const errorMessage = rowState?.error || '';
  // Saving/saved/error are row-level statuses, but only the cells that were
  // actually part of the save should reflect them. Other cells in the row stay
  // idle (or keep their own dirty marker if the user has typed more since).
  const cellParticipated = (rowState?.inFlightTiers || []).includes(cellIndex);
  const status = cellParticipated ? rowStatus : 'idle';

  const isZero = !isDirty && rate === 0;

  const onChange = useCallback((e) => {
    dispatch(setCellValue({
      rowKey, tierIndex: cellIndex, value: e.target.value, savedRate: rate,
    }));
  }, [dispatch, rowKey, cellIndex, rate]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      dispatch(revertRow({ rowKey }));
      inputRef.current?.blur();
    }
  };

  const { bg, ring } = accentForStatus(status, isDirty);
  const tooltip = errorMessage
    || (isZero ? 'Zero rate — confirm intentional.' : '');
  const numberColor = isZero ? 'warning.main' : 'text.primary';

  // Right-anchored cluster: tinted pill wraps `$` + input so the marker hugs
  // the value. The outer flex keeps the cell right-aligned with the column.
  const cellInner = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          bgcolor: bg,
          outline: '1.5px solid',
          outlineColor: ring,
          transition: 'background-color 450ms ease-out, outline-color 450ms ease-out',
        }}
      >
        <Typography component="span" sx={{ fontSize: 14, color: numberColor, lineHeight: 1 }}>
          $
        </Typography>
        <InputBase
          inputRef={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          type="text"
          inputMode="decimal"
          endAdornment={status === 'saving' ? (
            <CircularProgress size={12} sx={{ ml: 0.5 }} />
          ) : null}
          inputProps={{
            'aria-label': ariaLabel,
            style: {
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              padding: 0,
              width: '5ch',
            },
          }}
          sx={{
            fontSize: 14,
            color: numberColor,
          }}
        />
      </Box>
    </Box>
  );

  // Always render the Tooltip wrapper (disabled when there's no message) so the
  // input's position in the tree is stable. Conditionally wrapping/unwrapping
  // remounts the InputBase — which steals focus mid-typing (e.g. the moment a
  // zero cell's "Zero rate" tooltip disappears as the first digit is entered).
  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow
      disableHoverListener={!tooltip}
      disableFocusListener={!tooltip}
      disableTouchListener={!tooltip}
    >
      {cellInner}
    </Tooltip>
  );
};

export default PricingRateCell;
